import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, isRefreshRejected } from '@services/auth/auth.service';
import { Observable, Subject, switchMap, take, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

const TOKEN_KEY = 'JWT_TOKEN';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Mutex shared across all requests: when the access token expires and N
// requests arrive with simultaneous 401s, only ONE refresh is triggered and the
// rest wait on this subject to retry with the new token.
//
// It is a `Subject` recreated per cycle, not a shared `BehaviorSubject`: the
// waiters used to filter out `null`, which is exactly what the failure branch
// emitted — so when the refresh failed they never got a value, never got an
// error and never completed. N-1 requests hung forever with their spinners
// turning while only the first one navigated to /login.
let isRefreshing = false;
let refreshResult: Subject<string> | null = null;

const isAuthEndpoint = (url: string): boolean =>
	url.includes('/auth/login') || url.includes('/auth/logout') || url.includes('/auth/refresh');

// Endpoints where a 401 must NOT force logout: the user is still authenticated
// but the operation fails due to a specific credential (wrong current password,
// wrong MFA code). The error propagates to the component to show it in the UI.
const skipLogoutOn401 = (url: string): boolean => url.includes('/password') || url.includes('/mfa/verify');

/**
 * 401s that are NOT an expired session: a limited candidate (the legacy video
 * hold, or a migrated one who has not finished — or is waiting for review). The
 * token is fine; the portal is just not open to them yet. Refreshing and
 * retrying would hit the same wall, so they go to the wizard, which is where
 * their portal is.
 */
const LIMITED_CANDIDATE_CODES = new Set([
	'CANDIDATE_PENDING_VIDEO',
	'CANDIDATE_PROFILE_INCOMPLETE',
	'CANDIDATE_UNDER_REVIEW',
]);

/**
 * Una petición que muere SIN respuesta (`status: 0`) no es un error del
 * servidor: es que no llegó. Casi siempre es el socket keep-alive que un
 * intermedio cerró por inactividad, y el síntoma clásico es "la primera falla y
 * la segunda va bien" — el cliente lo describió como "al pasar un rato
 * desaparece la info".
 *
 * Se reintenta UNA vez, y solo en lecturas: un POST que no sabemos si llegó no
 * se puede repetir a ciegas, porque podría cobrar, invitar o publicar dos veces.
 */
const isRetriableRead = (request: HttpRequest<unknown>): boolean =>
	request.method === 'GET' || request.method === 'HEAD';

const retryOnDeadConnection = <T>(request: HttpRequest<unknown>) =>
	retry<T>({
		count: isRetriableRead(request) ? 1 : 0,
		delay: (error: unknown) => {
			const status = (error as HttpErrorResponse)?.status;
			if (status !== 0) throw error;
			// Un respiro corto: el socket nuevo se abre en el reintento.
			return timer(400);
		},
	});

const addAuthHeader = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
	request.clone({
		withCredentials: false,
		setHeaders: {
			Authorization: `Bearer ${token}`,
			Accept: 'application/json',
			...(!(request.body instanceof FormData) && {
				'Content-Type': 'application/json',
			}),
		},
	});

export const AuthInterceptor: HttpInterceptorFn = (
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const router = inject(Router);
	const authService = inject(AuthService);

	if (isAuthEndpoint(request.url)) {
		return next(request);
	}

	const token = localStorage.getItem(TOKEN_KEY);

	if (!token) {
		return next(request).pipe(retryOnDeadConnection(request));
	}

	if (!token.includes('.')) {
		localStorage.removeItem(TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		router.navigate(['/login']);
		return throwError(() => new Error('Invalid token'));
	}

	return next(addAuthHeader(request, token)).pipe(
		retryOnDeadConnection(request),
		catchError((error: HttpErrorResponse) => {
			if (error.status !== 401) {
				return throwError(() => error);
			}

			if (skipLogoutOn401(request.url)) {
				return throwError(() => error);
			}

			const code = (error.error as { message?: unknown } | null)?.message;
			if (typeof code === 'string' && LIMITED_CANDIDATE_CODES.has(code)) {
				router.navigate(['/candidate/onboarding']);
				return throwError(() => error);
			}

			const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
			if (!refreshToken) {
				localStorage.removeItem(TOKEN_KEY);
				router.navigate(['/login']);
				return throwError(() => error);
			}

			return handle401(request, next, authService, router);
		}),
	);
};

function handle401(
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
	authService: AuthService,
	router: Router,
): Observable<HttpEvent<unknown>> {
	if (isRefreshing && refreshResult) {
		return refreshResult.pipe(
			take(1),
			switchMap((newToken) => next(addAuthHeader(request, newToken))),
		);
	}

	isRefreshing = true;
	const result = new Subject<string>();
	refreshResult = result;

	return authService.refreshToken().pipe(
		switchMap((response: { access_token: string }) => {
			isRefreshing = false;
			refreshResult = null;
			result.next(response.access_token);
			result.complete();
			return next(addAuthHeader(request, response.access_token));
		}),
		catchError((refreshError) => {
			isRefreshing = false;
			refreshResult = null;
			// The queued requests have to LEARN that the session is gone. A
			// Subject replays its terminal state, so even one that subscribes
			// after this line gets the error instead of waiting forever.
			result.error(refreshError);
			// Solo se borra la sesión si el servidor RECHAZÓ el refresh token. Si
			// simplemente no pudo contestar (429, 5xx, sin red) el token sigue
			// siendo bueno: se deja donde está y falla solo esta petición. Antes
			// cualquier fallo aquí vaciaba el almacenamiento y mandaba al login,
			// que es lo que se veía como "me ha echado sin tocar nada".
			if (isRefreshRejected(refreshError)) {
				localStorage.removeItem(TOKEN_KEY);
				localStorage.removeItem(REFRESH_TOKEN_KEY);
				router.navigate(['/login']);
			}
			return throwError(() => refreshError);
		}),
	);
}
